/**
 * Manifest API wrappers + client-side Ed25519 verification.
 *
 * The server signs every approved version's `ApprovalManifest` with the
 * tenant's Ed25519 key; the canonical JSON is RFC 8785 (JCS). Players
 * verify before render. The admin SPA performs the same verify as a
 * defense-in-depth signal for operators ("manifest is valid right now,
 * from your browser, against the server-advertised public key").
 *
 * Endpoints (planned — siblings to `resources` / `audit` controllers):
 *
 *   GET  /v1/admin/manifests/:id     → { canonical_json, signature_b64,
 *                                         signing_key_id, kind }
 *   GET  /v1/admin/signing-keys      → { items: PublicKeyEntry[] }
 *
 * Note: this module intentionally implements Ed25519 verify locally
 * (via native WebCrypto Ed25519) rather than importing
 * `@displaycms/crypto`'s `browserCrypto.browserVerify`. The behavior is
 * equivalent — `browserCrypto` prefers native WebCrypto and falls back
 * to `@noble/ed25519` — but adding the crypto workspace package to
 * `apps/admin` requires editing `package.json`, the build/test configs,
 * AND `pnpm install`, all of which are out of scope for this task.
 * Modern browsers (Chrome/Edge 113+, Safari 17+) ship native Ed25519,
 * which covers the operator workstation target.
 */

import type { PublicKeyEntry } from '@displaycms/contracts';

import { apiGet } from '@/api/client';

export interface ManifestEnvelope {
  /** RFC 8785 (JCS) canonical JSON of the manifest body. */
  canonical_json: string;
  /** Standard-base64 (NOT base64url) detached Ed25519 signature. */
  signature_b64: string;
  /** UUID of the signing key in the tenant key-ring. */
  signing_key_id: string;
  /** Manifest discriminator (asset / source / composer). */
  kind: string;
}

export function getManifest(
  id: string,
  options?: { signal?: AbortSignal },
): Promise<ManifestEnvelope> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  return apiGet<ManifestEnvelope>(`/v1/admin/manifests/${id}`, opts);
}

export function listPublicKeys(
  options?: { signal?: AbortSignal },
): Promise<PublicKeyEntry[]> {
  const opts = options?.signal ? { signal: options.signal } : undefined;
  // The server returns a plain array, not { items: [...] }.
  return apiGet<PublicKeyEntry[]>('/v1/admin/signing-keys', opts);
}

// ---------------------------------------------------------------------------
// Verification.
// ---------------------------------------------------------------------------

export interface VerifyManifestInput {
  canonicalJson: string;
  signatureB64: string;
  publicKeyB64: string;
}

function b64ToBytes(b64: string): Uint8Array {
  // Standard base64. The server emits "+/=" (NOT base64url).
  const bin = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i) & 0xff;
  return out;
}

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

/**
 * Verifies an Ed25519 detached signature over the (already JCS-canonical)
 * manifest JSON. Returns `false` on any error — invalid base64, missing
 * WebCrypto, signature mismatch — so the UI can render a single red
 * "invalid signature" indicator regardless of root cause.
 *
 * This is a defense-in-depth check; the server is the authority on
 * whether a manifest exists at all.
 */
export async function verifyManifestClient(
  input: VerifyManifestInput,
): Promise<boolean> {
  try {
    if (typeof crypto === 'undefined' || !crypto.subtle) return false;
    const sig = b64ToBytes(input.signatureB64);
    const pub = b64ToBytes(input.publicKeyB64);
    if (sig.length !== 64) return false;
    if (pub.length !== 32) return false;
    const msg = utf8(input.canonicalJson);
    const key = await crypto.subtle.importKey(
      'raw',
      pub as unknown as BufferSource,
      { name: 'Ed25519' },
      false,
      ['verify'],
    );
    return await crypto.subtle.verify(
      { name: 'Ed25519' },
      key,
      sig as unknown as BufferSource,
      msg as unknown as BufferSource,
    );
  } catch {
    return false;
  }
}
